// @ts-nocheck
import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAdminOrgScope } from "@/hooks/useAdminOrgScope";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Table2, Copy, Trash2, Send } from "lucide-react";
import { toast } from "sonner";
import SheetShareLinksPanel from "@/components/events/SheetShareLinksPanel";

const SCOPES = [
  { value: "event", label: "Registrations", path: "event-sheet" },
  { value: "payment", label: "Payments", path: "payment-sheet" },
  { value: "roster", label: "Roster", path: "roster-sheet" },
];
const PERMISSIONS = [
  { value: "view", label: "View only" },
  { value: "comment", label: "Comment" },
  { value: "edit", label: "Edit" },
];
const pathFor = (scope: string) => SCOPES.find((s) => s.value === scope)?.path || "event-sheet";
const sheetUrl = (scope: string, token: string) => `${window.location.origin}/${pathFor(scope)}/${token}`;
const portalUrl = (email: string) => `${window.location.origin}/my-sheets?email=${encodeURIComponent(email)}`;

export default function LiveSheetsHub() {
  const { scopeOrgId, ready } = useAdminOrgScope();
  const { user } = useAuth();
  const qc = useQueryClient();

  const [scope, setScope] = useState("event");
  const [eventId, setEventId] = useState("");
  const [label, setLabel] = useState("");
  const [permission, setPermission] = useState("edit");
  const [emails, setEmails] = useState("");
  const [expiry, setExpiry] = useState("");
  const [showCheckin, setShowCheckin] = useState(false);
  const [creating, setCreating] = useState(false);

  const { data: events = [] } = useQuery({
    queryKey: ["hub-events", scopeOrgId],
    enabled: ready,
    queryFn: async () => {
      let q = (supabase as any).from("v_event_admin_dashboard").select("*").order("start_date", { ascending: false });
      if (scopeOrgId) q = q.eq("organization_id", scopeOrgId);
      const { data, error } = await q;
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: tokens = [], isLoading: tokensLoading } = useQuery({
    queryKey: ["hub-tokens", scopeOrgId],
    enabled: ready,
    queryFn: async () => {
      let q = (supabase as any)
        .from("event_sheet_tokens")
        .select("*")
        .neq("label", "__internal__")
        .order("created_at", { ascending: false });
      if (scopeOrgId) q = q.eq("organization_id", scopeOrgId);
      const { data, error } = await q;
      if (error) throw error;
      return data ?? [];
    },
  });

  const eventNameById = useMemo(() => {
    const m: Record<string, string> = {};
    events.forEach((e: any) => (m[e.id] = e.name));
    return m;
  }, [events]);

  const refetch = () => {
    qc.invalidateQueries({ queryKey: ["hub-tokens", scopeOrgId] });
  };

  const create = async () => {
    if (!eventId) return toast.error("Pick an event");
    setCreating(true);
    const allowed = emails
      .split(/[\n,]/)
      .map((e) => e.trim().toLowerCase())
      .filter(Boolean);
    try {
      const { data, error } = await (supabase as any)
        .from("event_sheet_tokens")
        .insert({
          event_id: eventId,
          scope,
          permission,
          label: label || null,
          allowed_emails: allowed.length ? allowed : null,
          expires_at: expiry ? new Date(expiry).toISOString() : null,
          show_checkin: showCheckin,
          created_by: user?.id ?? null,
        })
        .select("token")
        .single();
      if (error) throw error;

      const link = sheetUrl(scope, data.token);
      if (allowed.length) {
        await Promise.all(
          allowed.map((to) =>
            (supabase as any).functions.invoke("send-system-message", {
              body: {
                recipient: { email: to },
                event_id: eventId,
                channels: ["email"],
                override_subject: `A live sheet was shared with you: ${eventNameById[eventId] || ""}`,
                override_body_html: `
                  <p>You've been given <b>${permission}</b> access to a live sheet for
                  <b>${eventNameById[eventId] || "an event"}</b>.</p>
                  <p><a href="${link}">Open the sheet directly</a></p>
                  <p>Or visit your <a href="${portalUrl(to)}">My Sheets portal</a> to see everything shared with you.</p>`,
              },
            })
          )
        );
        toast.success(`Share link created and emailed to ${allowed.length} recipient(s)`);
      } else {
        await navigator.clipboard?.writeText(link).catch(() => {});
        toast.success("Share link created (copied to clipboard)");
      }
      setLabel("");
      setEmails("");
      setExpiry("");
      refetch();
    } catch (e: any) {
      toast.error(e.message || "Failed to create token");
    } finally {
      setCreating(false);
    }
  };

  const setRevoked = async (id: string, revoked: boolean) => {
    const { error } = await (supabase as any)
      .from("event_sheet_tokens")
      .update({ revoked, revoked_at: revoked ? new Date().toISOString() : null })
      .eq("id", id);
    if (error) return toast.error(error.message);
    refetch();
  };

  const del = async (id: string) => {
    const { error } = await (supabase as any).from("event_sheet_tokens").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Deleted");
    refetch();
  };

  return (
    <div className="max-w-full space-y-6 p-6">
      <div className="flex items-center gap-3">
        <Table2 className="h-6 w-6 text-[#002E62]" />
        <div>
          <h1 className="text-2xl font-bold">Live Sheets</h1>
          <p className="text-sm text-muted-foreground">Create shareable, inline-editable sheets for your events.</p>
        </div>
      </div>

      <SheetShareLinksPanel orgId={scopeOrgId} />


      <Card>
        <CardHeader><CardTitle className="text-base">Create a share link</CardTitle></CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1">
            <Label>Scope</Label>
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {SCOPES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Event</Label>
            <Select value={eventId} onValueChange={setEventId}>
              <SelectTrigger><SelectValue placeholder="Select event" /></SelectTrigger>
              <SelectContent>
                {events.map((e: any) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Label (optional)</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Front desk" />
          </div>
          <div className="space-y-1">
            <Label>Permission</Label>
            <Select value={permission} onValueChange={setPermission}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {PERMISSIONS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1 md:col-span-2">
            <Label>Restrict to emails (optional, one per line)</Label>
            <Textarea rows={3} value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="coach@club.com" />
            <p className="text-xs text-muted-foreground">Leave blank for anyone-with-the-link access. Listed emails get an invite email.</p>
          </div>
          <div className="space-y-1">
            <Label>Expiry (optional)</Label>
            <Input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} />
          </div>
          {scope === "roster" && (
            <div className="flex items-center gap-2 pt-6">
              <Switch checked={showCheckin} onCheckedChange={setShowCheckin} id="ci" />
              <Label htmlFor="ci">Show check-in column</Label>
            </div>
          )}
          <div className="md:col-span-2">
            <Button onClick={create} disabled={creating} className="bg-[#002E62] hover:bg-[#002E62]/90">
              {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
              Create share link
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Existing links</CardTitle></CardHeader>
        <CardContent>
          {tokensLoading ? (
            <div className="flex items-center py-6 text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…</div>
          ) : tokens.length === 0 ? (
            <p className="text-sm text-muted-foreground">No share links yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-2 pr-3">Event</th>
                    <th className="py-2 pr-3">Scope</th>
                    <th className="py-2 pr-3">Permission</th>
                    <th className="py-2 pr-3">Label</th>
                    <th className="py-2 pr-3">Restricted</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tokens.map((t: any) => (
                    <tr key={t.id} className="border-b">
                      <td className="py-2 pr-3">{eventNameById[t.event_id] || "—"}</td>
                      <td className="py-2 pr-3">{SCOPES.find((s) => s.value === t.scope)?.label || t.scope}</td>
                      <td className="py-2 pr-3">{t.permission}</td>
                      <td className="py-2 pr-3">{t.label || "—"}</td>
                      <td className="py-2 pr-3">{t.allowed_emails?.length ? `${t.allowed_emails.length} email(s)` : "Anyone"}</td>
                      <td className="py-2 pr-3">
                        {t.revoked ? <Badge variant="destructive">Revoked</Badge> : <Badge className="bg-green-600">Active</Badge>}
                      </td>
                      <td className="py-2 pr-3">
                        <div className="flex items-center gap-1">
                          <Button size="icon" variant="ghost" title="Copy link"
                            onClick={() => { navigator.clipboard?.writeText(sheetUrl(t.scope, t.token)); toast.success("Copied"); }}>
                            <Copy className="h-4 w-4" />
                          </Button>
                          {t.revoked ? (
                            <Button size="sm" variant="outline" onClick={() => setRevoked(t.id, false)}>Restore</Button>
                          ) : (
                            <Button size="sm" variant="outline" onClick={() => setRevoked(t.id, true)}>Revoke</Button>
                          )}
                          <Button size="icon" variant="ghost" title="Delete" onClick={() => del(t.id)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
